import type { FromModel, ModelDefinition } from "@/__main__/data-model.mjs";
import createModel, { Silent, Tuple } from "@/__main__/data-model.mjs";

const COLUMN_COUNT = 7;
const COLUMNS = Array.from({ length: COLUMN_COUNT }, () => String);

const buildModel = Tuple(COLUMNS) satisfies ModelDefinition;

const columnModel = {
  name: String,
  position: Number,
  typeName: String,
  typeText: String,
};

const Entry = {
  mean: Number,
  stdev: Number,
  count: Number,
};
const Stats = {
  assists: Entry,
  deaths: Entry,
  kills: Entry,
  kda: Entry,
  kp: Entry,
  gold_spent: Entry,
  total_damage_done_to_champions: Entry,
  total_damage_taken: Entry,
  ward_kills: Entry,
  yellow_trinket_placed: Entry,
  sight_ward_placed: Entry,
  control_ward_placed: Entry,
  creep_score: Entry,
  jungle_creep_score: Entry,
  total_gold: Entry,
  xp: Entry,
  level: Entry,
} satisfies ModelDefinition;

const ChampionByMinuteModel = createModel({
  data: {
    executeDatabricksQuery: {
      payload: {
        manifest: {
          schema: {
            columnCount: Number,
            columns: [columnModel],
          },
        },
        result: {
          chunkIndex: Silent(Number),
          dataArray: [buildModel],
          rowCount: Silent(Number),
          rowOffset: Silent(Number),
        },
      },
    },
  },
}).transform([Stats], (data) => {
  const {
    data: { executeDatabricksQuery },
  } = data;

  const { columns = [] } =
    executeDatabricksQuery?.payload?.manifest?.schema || {};

  const list = executeDatabricksQuery?.payload?.result?.dataArray || [];

  const stats = list.map((c) => {
    const result = {
      patch: "",
      queue_id: null,
      champion_id: 0,
      championId: 0,
      tier: null,
      individual_position: null,
      stats_by_minute: {
        0: {
          assists: {
            mean: 0,
            stdev: 0,
            count: 0,
          },
          deaths: {
            mean: 0,
            stdev: 0,
            count: 0,
          },
          kills: {
            mean: 0,
            stdev: 0,
            count: 0,
          },
          kda: {
            mean: 0,
            stdev: 0,
            count: 0,
          },
          kp: {
            mean: 0,
            stdev: 0,
            count: 0,
          },
          gold_spent: {
            mean: 0,
            stdev: 0,
            count: 0,
          },
          total_damage_done_to_champions: {
            mean: 0,
            stdev: 0,
            count: 0,
          },
          total_damage_taken: {
            mean: 0,
            stdev: 0,
            count: 0,
          },
          ward_kills: {
            mean: 0,
            stdev: 0,
            count: 0,
          },
          yellow_trinket_placed: {
            mean: 0,
            stdev: 0,
            count: 0,
          },
          sight_ward_placed: {
            mean: 0,
            stdev: 0,
            count: 0,
          },
          control_ward_placed: {
            mean: 0,
            stdev: 0,
            count: 0,
          },
          creep_score: {
            mean: 0,
            stdev: 0,
            count: 0,
          },
          jungle_creep_score: {
            mean: 0,
            stdev: 0,
            count: 0,
          },
          total_gold: {
            mean: 0,
            stdev: 0,
            count: 0,
          },
          xp: {
            mean: 0,
            stdev: 0,
            count: 0,
          },
          level: {
            mean: 0,
            stdev: 0,
            count: 0,
          },
        },
      },
    };
    for (const column of columns) {
      const value: string = (c[column.position] || "") as unknown as string;
      result[column.name] = castType(column.typeName, value);
    }

    result.championId = result.champion_id;

    return result;
  });

  return Object.values(stats[0]?.stats_by_minute || {}).map((s) => {
    return {
      assists: s.assists,
      deaths: s.deaths,
      kills: s.kills,
      kda: s.kda,
      kp: s.kp,
      gold_spent: s.gold_spent,
      total_damage_done_to_champions: s.total_damage_done_to_champions,
      total_damage_taken: s.total_damage_taken,
      ward_kills: s.ward_kills,
      yellow_trinket_placed: s.yellow_trinket_placed,
      sight_ward_placed: s.sight_ward_placed,
      control_ward_placed: s.control_ward_placed,
      creep_score: s.creep_score,
      jungle_creep_score: s.jungle_creep_score,
      total_gold: s.total_gold,
      xp: s.xp,
      level: s.level,
    };
  });
});

export default ChampionByMinuteModel;

export type ChampionByMinuteStats = FromModel<typeof ChampionByMinuteModel>;

function castType(typeText: string, value: string) {
  switch (typeText) {
    case "BOOLEAN":
      return value === "true";
    case "DOUBLE":
    case "BIGINT":
    case "LONG":
    case "INT":
      return value ? Number(value) : 0;
    case "MAP":
      return value ? JSON.parse(value) : {};
    case "ARRAY":
      return value ? JSON.parse(value) : [];
    case "STRUCT":
      return value ? JSON.parse(value) : {};
    case "STRING":
    case undefined:
    default:
      return value ?? "";
  }
}
